import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../config/site";

const links = [
  { id: "inicio", label: "Inicio" },
  { id: "sobre-mi", label: "Sobre mí" },
  { id: "servicios", label: "Servicios" },
  { id: "faq", label: "Preguntas frecuentes" },
  { id: "contacto", label: "Contacto" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-ivory/85 backdrop-blur-md border-b border-atento-blue/10 shadow-[0_2px_20px_rgba(27,59,111,0.05)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[76px]">
          <button
            onClick={() => go("inicio")}
            aria-label="Ir al inicio"
            data-testid="nav-logo-button"
            className="cursor-pointer"
          >
            <Logo />
          </button>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Navegación principal">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="text-sm font-accent font-medium text-charcoal/75 hover:text-atento-blue transition-colors duration-200 cursor-pointer"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => go("contacto")}
              data-testid="nav-cta-button"
              className="hidden sm:inline-flex items-center gap-2 bg-atento-blue hover:bg-atento-dark text-white font-accent font-semibold text-sm px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer"
            >
              Cuéntame qué necesitas
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-button"
              aria-label={open ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={open}
              className="lg:hidden p-2.5 rounded-full text-atento-blue hover:bg-sage-light transition-colors cursor-pointer"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-ivory/95 backdrop-blur-md border-b border-atento-blue/10"
            aria-label="Navegación móvil"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="text-left font-serif text-2xl font-medium text-atento-blue py-3 border-b border-atento-blue/8 last:border-0 cursor-pointer"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                onClick={() => go("contacto")}
                data-testid="mobile-nav-cta-button"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-atento-blue text-white font-accent font-semibold px-6 py-4 rounded-full cursor-pointer"
              >
                Cuéntame qué necesitas
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
